/**
 * NIGHTBOUND - training system configuration and pure helpers.
 *
 * Training never raises a stat instantly: sessions award discipline XP, and
 * stats only move when a discipline level threshold is crossed. Progress has
 * diminishing returns and a daily session cap, so grinding is bounded.
 */

import { clamp, clampNeed, round1 } from './constants'
import type { StatKey } from './species'

export const TRAINING_DISCIPLINES = [
  'STRENGTH',
  'ENDURANCE',
  'DEFENCE',
  'AGILITY',
] as const
export type TrainingDiscipline = (typeof TRAINING_DISCIPLINES)[number]

export interface TrainingDefinition {
  id: TrainingDiscipline
  name: string
  /** Which combat stat this discipline develops. */
  statKey: StatKey
  statLabel: string
  description: string
  flavour: string
  glyph: string
  accent: string
  xpPerSession: number
  /** Stat points gained each time the discipline levels up. */
  statGainPerLevel: number
  /**
   * Every session costs the same 15 energy, which is what actually paces
   * repeated training (the cooldown is short). Hunger/hygiene still slip.
   */
  cost: { energy: number; hunger: number; hygiene: number }
}

export const TRAINING: Record<TrainingDiscipline, TrainingDefinition> = {
  STRENGTH: {
    id: 'STRENGTH',
    name: 'Strength',
    statKey: 'attack',
    statLabel: 'Attack',
    description: 'Weight, grip, and the will to close distance.',
    flavour: 'It learns how much force it takes to break something.',
    glyph: '⚒',
    accent: '#a8452f',
    xpPerSession: 26,
    statGainPerLevel: 1,
    cost: { energy: 15, hunger: 6, hygiene: 4 },
  },
  ENDURANCE: {
    id: 'ENDURANCE',
    name: 'Endurance',
    statKey: 'hp',
    statLabel: 'Health',
    description: 'Long punishing work that widens how much it can take.',
    flavour: 'It stops shaking after the fourth set.',
    glyph: '🜂',
    accent: '#6f7a4a',
    xpPerSession: 24,
    statGainPerLevel: 4,
    cost: { energy: 15, hunger: 7, hygiene: 4 },
  },
  DEFENCE: {
    id: 'DEFENCE',
    name: 'Defence',
    statKey: 'defence',
    statLabel: 'Defence',
    description: 'Bracing, guarding, and refusing to be moved.',
    flavour: 'It stops flinching.',
    glyph: '🛡',
    accent: '#6b6f78',
    xpPerSession: 25,
    statGainPerLevel: 1,
    cost: { energy: 15, hunger: 6, hygiene: 4 },
  },
  AGILITY: {
    id: 'AGILITY',
    name: 'Agility',
    statKey: 'speed',
    statLabel: 'Speed',
    description: 'Sprints, turns, and sudden violence of movement.',
    flavour: 'It moves before you have finished deciding.',
    glyph: '🜁',
    accent: '#8b8f6a',
    xpPerSession: 22,
    statGainPerLevel: 1,
    cost: { energy: 15, hunger: 6, hygiene: 5 },
  },
}

export const TRAINING_LIST: TrainingDefinition[] = TRAINING_DISCIPLINES.map((d) => TRAINING[d])

/** Discipline XP required to advance from `level` to the next. */
export function trainingXpForLevel(level: number): number {
  return Math.round(60 * Math.pow(Math.max(1, level), 1.4))
}

/**
 * How much of a session the creature actually gets out of it. A hungry,
 * filthy, exhausted creature learns very little.
 */
export function trainingIntensity(input: {
  hunger: number
  energy: number
  hygiene: number
  bond: number
}): { multiplier: number; label: string } {
  if (input.energy < 12) return { multiplier: 0.35, label: 'Too exhausted to focus' }
  let multiplier = 1
  if (input.hunger < 25) multiplier *= 0.6
  else if (input.hunger < 50) multiplier *= 0.85
  if (input.hygiene < 25) multiplier *= 0.7
  else if (input.hygiene < 50) multiplier *= 0.9
  if (input.bond < 25) multiplier *= 0.75
  else if (input.bond > 75) multiplier *= 1.1

  const label =
    multiplier >= 1.05
      ? 'Focused and eager'
      : multiplier >= 0.95
        ? 'Steady work'
        : multiplier >= 0.75
          ? 'Distracted'
          : 'Barely trying'
  return { multiplier: clamp(multiplier, 0.2, 1.25), label }
}

/** Sessions get less productive as the day goes on. */
export function trainingDiminishingReturns(sessionsToday: number, dailyCap: number): number {
  if (sessionsToday <= 0) return 1
  const ratio = clamp(sessionsToday / dailyCap, 0, 1)
  return clamp(1 - ratio * 0.55, 0.3, 1)
}

export interface TrainingOutcome {
  discipline: TrainingDiscipline
  xpGained: number
  intensityLabel: string
  previousLevel: number
  newLevel: number
  levelsGained: number
  statKey: StatKey
  statGained: number
  cappingApplied: boolean
}

/** Pure resolver used by the server. No database, no random numbers. */
export function resolveTrainingSession(input: {
  discipline: TrainingDiscipline
  progressLevel: number
  progressXp: number
  sessionsToday: number
  dailyCap: number
  intensityMultiplier: number
  toolXpMultiplier: number
}): {
  progress: { level: number; xp: number }
  outcome: Omit<TrainingOutcome, 'intensityLabel' | 'cappingApplied'>
} {
  const definition = TRAINING[input.discipline]
  const diminished = trainingDiminishingReturns(input.sessionsToday, input.dailyCap)
  const xpGained = round1(
    definition.xpPerSession * input.intensityMultiplier * input.toolXpMultiplier * diminished,
  )

  let level = input.progressLevel
  let xp = input.progressXp + xpGained
  let levelsGained = 0
  let guard = 0
  while (xp >= trainingXpForLevel(level) && guard < 50) {
    xp -= trainingXpForLevel(level)
    level += 1
    levelsGained += 1
    guard += 1
  }

  return {
    progress: { level, xp: round1(xp) },
    outcome: {
      discipline: input.discipline,
      xpGained,
      previousLevel: input.progressLevel,
      newLevel: level,
      levelsGained,
      statKey: definition.statKey,
      statGained: levelsGained * definition.statGainPerLevel,
    },
  }
}

/** Effective needs after a training session has been paid for. */
export function applyTrainingCost(
  needs: { energy: number; hunger: number; hygiene: number },
  discipline: TrainingDiscipline,
): { energy: number; hunger: number; hygiene: number } {
  const cost = TRAINING[discipline].cost
  return {
    energy: clampNeed(needs.energy - cost.energy),
    hunger: clampNeed(needs.hunger - cost.hunger),
    hygiene: clampNeed(needs.hygiene - cost.hygiene),
  }
}
